import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  DATA_CONTROLLER_CONTACT_EMAIL,
  DATA_CONTROLLER_NAME,
  LEGAL_DOCUMENT_VERSION,
  MIN_USER_AGE,
} from '@/config/legal';

/**
 * Политика обработки персональных данных.
 *
 * Экран не зависит от статуса Supabase/сессии намеренно — правовой
 * документ обязан открываться даже если backend недоступен или не
 * настроен, поэтому маршрут /privacy живёт вне AppShell (см. router.tsx).
 *
 * Оператор и контакт — в config/legal.ts. Разделы 4–5 описывают устройство
 * после переноса на свой сервер в РФ (ADR-048), проверку фото на нём
 * (ADR-049) и передачу в США через мост (docs/rkn-instrukciya-podachi-
 * portretnaya-studiya.md, раздел 8). Меняется получатель или маршрут —
 * меняются этот текст, TRANSFER_CONSENT_TEXT и версия документов.
 */
export function PrivacyPolicyPage() {
  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-6 py-10 text-sm text-text-2">
      <div>
        <Link to="/" className="text-xs text-accent hover:underline">
          ← На главную
        </Link>
        <h1 className="mt-3 font-display text-h1 text-text">
          Политика обработки персональных данных
        </h1>
        <p className="mt-1 text-xs text-text-3">
          Редакция от {LEGAL_DOCUMENT_VERSION}. Действует в отношении сервиса «Астра студия» (АСТРА space | Студия).
        </p>
      </div>

      <Section title="1. Оператор">
        <p>
          Оператором персональных данных является {DATA_CONTROLLER_NAME}. Обращения по вопросам
          обработки персональных данных — на адрес{' '}
          <a href={`mailto:${DATA_CONTROLLER_CONTACT_EMAIL}`} className="text-accent hover:underline">
            {DATA_CONTROLLER_CONTACT_EMAIL}
          </a>
          .
        </p>
        <p className="mt-2">
          Сервис предназначен для пользователей от {MIN_USER_AGE} лет, возраст подтверждается при
          первом запуске. Сознательно данные лиц младше этого возраста не обрабатываются; если вы
          узнали, что ребёнок пользуется сервисом, напишите на адрес выше — аккаунт и данные будут
          удалены.
        </p>
      </Section>

      <Section title="2. Какие данные обрабатываются и зачем">
        <Table
          rows={[
            [
              'Загруженные фотографии',
              'Создание AI-портрета/фотосессии. Не используются для установления личности — только как исходный материал для генерации.',
            ],
            [
              'Сгенерированные изображения',
              'Хранение результата: история, избранное, скачивание, повтор.',
            ],
            [
              'Анонимный идентификатор пользователя, идентификатор устройства',
              'Работа сервиса между визитами: баланс, история, избранное — без регистрации и пароля.',
            ],
            [
              'Идентификатор Telegram или VK (после проверки подписи хоста)',
              'Связывание одного человека с несколькими каналами (Web/Telegram/VK) без потери истории и баланса; в будущем — доставка уведомлений.',
            ],
            [
              'Хеш IP-адреса и отпечатка устройства',
              'Защита бесплатных генераций от автоматической накрутки — только хеш, не сырой IP.',
            ],
            [
              'Реферальный код и факт перехода по приглашению',
              'Начисление наград за приглашения — только после первой успешной генерации приглашённого.',
            ],
            [
              'Журнал операций с балансом (append-only)',
              'Учёт кредитов, разрешение споров по начислениям и списаниям.',
            ],
            [
              'Журнал согласий',
              'Доказательство факта и содержания данного согласия — обязанность оператора, не пользователя.',
            ],
            [
              'События использования сервиса (какие экраны открыты, какие действия совершены)',
              'Понимание того, что в продукте работает, а что нет.',
            ],
            [
              'Текст пожеланий новых коллекций (если вы его отправили)',
              'Решение, какие коллекции добавить. Читает только оператор; к фотографиям и генерации не относится и удаляется вместе с аккаунтом.',
            ],
          ]}
        />
      </Section>

      <Section title="3. Фотографии людей и биометрические персональные данные">
        <p>
          Загруженные фотографии не используются сервисом для установления личности изображённого
          человека — только как материал для генерации портрета. По этой причине они не относятся
          к биометрическим персональным данным в значении ст. 11 152-ФЗ. Если в будущем в сервисе
          появится функция сравнения или узнавания лиц (например, защита от повторной регистрации
          по фотографии), эта классификация будет пересмотрена и согласие — запрошено заново, в
          письменной форме.
        </p>
        <p className="mt-2">
          Загружая фотографию с другими людьми в кадре, пользователь подтверждает, что вправе
          использовать эти фотографии — в том числе если на них есть другие люди. Ответственность
          за наличие такого права лежит на пользователе (см. Условия использования).
        </p>
      </Section>

      <Section title="4. Кто ещё обрабатывает данные">
        <ul className="list-inside list-disc space-y-2">
          <li>
            <span className="text-text">Хостинг.</span> Сервер, на котором работают база данных и
            хранилище файлов, арендован у российского хостинг-провайдера (ООО «АЙТИ БАЗИС», HOSTKEY)
            и находится в Российской Федерации. Хостинг-провайдер предоставляет оборудование и не
            имеет доступа к содержимому сервера.
          </li>
          <li>
            <span className="text-text">Проверка фотографий.</span> Перед созданием портрета
            фотография автоматически проверяется на том же сервере: есть ли в кадре люди, сколько
            их и нет ли недопустимого содержимого. Для этой проверки фотография никому не
            передаётся.
          </li>
          <li>
            <span className="text-text">Создание изображения.</span> Изображение создаёт нейросеть
            OpenAI (США). Запрос к ней идёт через технический сервер-посредник в США, арендованный у
            ООО «Баксэт» (JustHost). Передаются фотографии (без данных съёмки: места, устройства,
            времени) и текст задания, собранный сервисом из выбранных вами вариантов. Имя,
            идентификатор, профиль Telegram или VK и IP-адрес не передаются. Сервер-посредник не
            ведёт журнал запросов и не сохраняет их содержимое. Подробнее о передаче — в разделе 5.
          </li>
          <li>
            <span className="text-text">Telegram и VK.</span> При входе через Telegram или VK
            сервис получает от площадки ваш идентификатор и проверяет его подпись. Сами площадки
            обрабатывают ваши данные по своим правилам как самостоятельные операторы.
          </li>
        </ul>
        <p className="mt-2">
          Пока реальная генерация не включена, вместо нейросети работает тестовая заглушка и
          фотографии за пределы сервера сервиса не передаются.
        </p>
      </Section>

      <Section title="5. Где хранятся данные и передача за пределы РФ">
        <p>
          Все данные пользователей — профиль, баланс, история, загруженные фотографии и
          результаты — записываются и хранятся в базе данных на сервере, расположенном в Российской
          Федерации.
        </p>
        <p className="mt-2">
          За пределы РФ (в США) передаются только фотографии для создания конкретного изображения и
          только после отдельного согласия, которое вы даёте отметкой перед созданием. Получатели —
          OpenAI и технический сервер-посредник (см. раздел 4). США не входят в перечень стран,
          обеспечивающих адекватную защиту прав субъектов персональных данных.
        </p>
        <p className="mt-2">
          По опубликованным условиям OpenAI данные запросов к API могут храниться у получателя до
          30 дней для контроля злоупотреблений и по умолчанию не используются для обучения моделей.
          Оператор не может удалить запрос у получателя: удаление аккаунта в сервисе удаляет данные
          у нас, но не у него. Поэтому не загружайте снимки, которые не хотите передавать.
        </p>
        <p className="mt-2">
          Согласие на передачу можно отозвать в любой момент, удалив аккаунт в настройках или
          написав на адрес из раздела 1. После этого новые фотографии не передаются.
        </p>
      </Section>

      <Section title="6. Уведомление в Роскомнадзор">
        <p>
          Оператор уведомляет Роскомнадзор об обработке персональных данных и о трансграничной
          передаче до того, как они начинаются. Передача фотографий за пределы РФ начнётся не
          раньше, чем истечёт установленный законом срок рассмотрения уведомления.
        </p>
      </Section>

      <Section title="7. Срок хранения и удаление">
        <p>
          Данные хранятся, пока ими можно воспользоваться — то есть пока существует аккаунт. При
          удалении аккаунта (доступно в любой момент в настройках) большинство данных удаляется
          безвозвратно и немедленно: профиль, фотографии, результаты генераций, история, избранное,
          связанные аккаунты Telegram/VK. Часть записей — например, журнал операций с балансом или
          журнал согласий — может по закону храниться дольше срока жизни аккаунта, если это
          обосновано целью обработки (учёт, разрешение споров, доказательство согласия).
        </p>
      </Section>

      <Section title="8. Права субъекта персональных данных">
        <p>Пользователь вправе:</p>
        <ul className="mt-2 list-inside list-disc space-y-1">
          <li>получить сведения о том, какие его данные обрабатываются;</li>
          <li>потребовать уточнения, блокирования или уничтожения неверных или незаконно обрабатываемых данных;</li>
          <li>отозвать согласие на обработку в любой момент;</li>
          <li>удалить аккаунт и связанные с ним данные — самостоятельно, в настройках сервиса;</li>
          <li>обратиться с жалобой в Роскомнадзор, если считает свои права нарушенными.</li>
        </ul>
      </Section>

      <Section title="9. Cookies и локальные идентификаторы">
        <p>
          Сервис использует localStorage браузера для реферального кода, идентификатора устройства
          и признака запуска из Telegram/VK-приложения. Эти значения можно удалить очисткой данных
          сайта в браузере — часть механик (например, защита бесплатных генераций от накрутки) при
          этом продолжит работать по другим сигналам.
        </p>
      </Section>

      <Section title="10. Изменения документа">
        <p>
          При существенном изменении текста меняется дата редакции наверху страницы. Прежние
          согласия остаются привязанными к той редакции, под которой они были даны.
        </p>
      </Section>
    </main>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-semibold text-text">{title}</h2>
      <div className="mt-2 leading-relaxed">{children}</div>
    </section>
  );
}

function Table({ rows }: { rows: [string, string][] }) {
  return (
    <div className="mt-2 flex flex-col gap-3 rounded-xl border border-border p-3">
      {rows.map(([label, description]) => (
        <div key={label}>
          <p className="text-text">{label}</p>
          <p className="text-xs text-text-3">{description}</p>
        </div>
      ))}
    </div>
  );
}
